// src/components/AIChat.js

import React, { useState } from 'react';
import { Rnd } from 'react-rnd';
import {
  Box,
  TextField,
  Button,
  Avatar,
  Typography,
  useTheme,
  IconButton,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import aiIcon from '../assets/images/ai_icon.png';
import faviconIcon from '../assets/images/favicon.png';
import { translateFieldNames } from '../utils/fieldTranslator';
import {
  designTokens,
  chatHeader,
  chatBackground,
  chatBubbleBot,
  chatBubbleUser,
  chatInputField,
  chatSendButton,
  liquidGlassCard,
  liquidGlassModal,
} from '../theme/designTokens';

/** 
 * サーバー応答メッセージが "純粋な JSON" かどうかを判定する 
 * - parseできかつ、オブジェクトまたは配列なら "純粋" とみなす
 */
function isPureJson(str) {
  try {
    const data = JSON.parse(str);
    // 配列 or オブジェクトであればtrue (＝純粋JSONとみなす)
    if (typeof data === 'object' && data !== null) {
      return true;
    }
  } catch (e) {}
  return false;
}

/** 特殊なメッセージ判定 (例: "###採用計画" が含まれる) */
function isSpecialMessage(text) {
  return text.includes('###') && text.includes('採用計画');
}

/** ローディング時の3点リーダー */
function TypingBubble() {
  const theme = useTheme();
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}
    >
      <Avatar
        alt="Bot"
        src={faviconIcon}
        sx={{
          width: 32,
          height: 32,
          mx: 1,
        }}
      />
      <Paper
        elevation={0}
        sx={{
          maxWidth: '70%',
          backgroundColor: '#f3f4f6',
          color: '#374151',
          borderRadius: '8px',
          p: 1.5,
          border: '1px solid #e5e7eb',
        }}
      >
        <Typography variant="body2">...</Typography>
      </Paper>
    </Box>
  );
}

/** メッセージ気泡 */
function ChatBubble({ message, featureKey }) {
  const theme = useTheme();
  const isBot = message.sender === 'bot';
  const [copied, setCopied] = React.useState(false);

  // "純粋なJSON"の場合は表示をスキップ
  if (isPureJson(message.text)) {
    return null;
  }

  // botメッセージの場合はフィールド名を翻訳
  const displayText = isBot ? translateFieldNames(message.text) : message.text;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isBot ? 'flex-start' : 'flex-end',
        mb: 1.5
      }}
    >
      <Avatar
        alt={isBot ? 'Bot' : 'User'}
        src={isBot ? faviconIcon : undefined}
        sx={{
          width: 32,
          height: 32,
          alignSelf: 'flex-end',
          order: isBot ? 1 : 2,
          mx: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      />
      <Box sx={{ order: isBot ? 2 : 1, maxWidth: '80%' }}>
        <Paper
          elevation={0}
          sx={{
            ...(isBot ? chatBubbleBot() : chatBubbleUser(featureKey)),
          }}
        >
          {isSpecialMessage(displayText) ? (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'white',
                border: `1px solid ${theme.palette.primary.light}`,
                borderRadius: 2,
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayText}
              </ReactMarkdown>
            </Box>
          ) : (
            <Box sx={{ '& p': { m: 0 } }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayText}
              </ReactMarkdown>
            </Box>
          )}
        </Paper>
        {isBot && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                opacity: 0.4,
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.2s',
                p: 0.25,
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * AIチャット(ポップアップ版)
 * 
 * Props: 
 *  - messages, userInput, setUserInput, isTyping, chatEndRef
 *  - chatLoading: 相談（AIに相談）送信中状態
 *  - reflectLoading: 採用計画を修正(要件反映)送信中状態
 *  - onChatSend: 相談(雑談)用送信ハンドラ
 *  - onReflectPlan: 要件反映(採用計画を修正)用送信ハンドラ
 *  - initialGuideMessage, iconTooltipMessage: 表示用
 *  - planFeatureEnabled: 採用計画修正機能を有効化するかどうか
 */
export default function AIChat({
  messages,
  userInput,
  setUserInput,
  isTyping,
  chatEndRef,
  chatLoading = false,
  reflectLoading = false,
  isLoading = false,
  onChatSend,
  onReflectPlan,
  initialGuideMessage = 'AIチャットへようこそ！何でも質問できます。',
  iconTooltipMessage = '',
  chatButtonLabel = 'AIに相談',
  planButtonLabel = '採用計画を修正',
  planFeatureEnabled = false, // 採用計画修正機能の有効/無効を制御する新しいプロパティ
  featureKey = null, // 機能別テーマカラー用キー
}) {
  const [isOpen, setIsOpen] = useState(false);

  // どちらの用途か: "chat" | "plan"
  // 計画修正機能が無効の場合は強制的に "chat" モードにする
  const [mode, setMode] = useState('chat');

  // 拡張メッセージ
  const extendedMessages =
    messages.length === 0
      ? [{ sender: 'bot', text: initialGuideMessage }]
      : messages;

  // 1) まだ閉じた状態 → AIアイコンだけ
  if (!isOpen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 120,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {iconTooltipMessage && (
          <Box
            sx={{
              mb: 2,
              backgroundColor: '#fef3c7',
              color: '#92400e',
              px: 2.5,
              py: 1.5,
              borderRadius: '8px',
              maxWidth: 280,
              textAlign: 'center',
              fontSize: '0.9rem',
              fontWeight: '500',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #fcd34d',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid #fef3c7'
              }
            }}
          >
            {iconTooltipMessage}
          </Box>
        )}

        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{
            width: 200,
            height: 200,
            backgroundColor: 'transparent',
            color: '#fff',
            borderRadius: 0,
            boxShadow: 'none',
            p: 0,
            overflow: 'visible',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            }
          }}
        >
          <Box
            component="img"
            src={aiIcon}
            alt="AI Icon"
            sx={{ width: 150, height: 150 }}
          />
        </IconButton>
      </Box>
    );
  }

  // 2) AIチャットポップアップ表示
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, pointerEvents: 'none' }}>
      <Rnd
        default={{
          x: window.innerWidth - 420,
          y: window.innerHeight - 580,
          width: 340,
          height: 480
        }}
        minWidth={300}
        minHeight={360}
        bounds="parent"
        style={{
          background: '#ffffff',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pointerEvents: 'auto'
        }}
        dragHandleClassName="chat-drag-handle"
      >
        {/* ヘッダー */}
        <Box
          className="chat-drag-handle"
          sx={{
            cursor: 'move',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            backgroundColor: featureKey && designTokens.featureThemes[featureKey]
              ? designTokens.featureThemes[featureKey].primary
              : '#249BE0',
            borderBottom: 'none',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
            AIアシスタント
          </Typography>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            sx={{
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* メッセージ一覧 */}
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            overflowY: 'auto',
            ...chatBackground(featureKey),
            background: '#f9fafb',
          }}
        >
          {extendedMessages.map((msg, i) => (
            <ChatBubble key={i} message={msg} featureKey={featureKey} />
          ))}

          {/* isTypingがtrueならローディング中アニメを表示 */}
          {isTyping && <TypingBubble />}
          <div ref={chatEndRef} />
        </Box>

        {/* 入力エリア */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e5e7eb',
            background: '#ffffff',
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="相談または修正内容を入力..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                if (!planFeatureEnabled || mode === 'chat') {
                  onChatSend && onChatSend();
                } else {
                  onReflectPlan && onReflectPlan();
                }
              }
            }}
            sx={{
              flexGrow: 1,
              mb: 1.5,
              ...chatInputField(featureKey),
            }}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
          />

          {/* トグルボタン(用途選択) & 送信ボタン */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {planFeatureEnabled ? (
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue) setMode(newValue);
                }}
                sx={{
                  borderRadius: '6px',
                  overflow: 'hidden',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 0,
                    color: '#6b7280',
                    fontWeight: 600,
                    px: 2,
                    py: 0.75,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: '#249BE0',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#1d8bc7',
                    },
                    '&:hover': {
                      backgroundColor: '#e5e7eb',
                    }
                  }
                }}
              >
                <ToggleButton value="chat">AIに相談</ToggleButton>
                <ToggleButton value="plan">計画を修正</ToggleButton>
              </ToggleButtonGroup>
            ) : (
              // 計画修正機能が無効の場合は空のスペースを表示
              <div></div>
            )}

            <Button
              onClick={() => {
                // 計画修正機能が無効の場合は常に chat モードとして扱う
                if (!planFeatureEnabled || mode === 'chat') {
                  onChatSend && onChatSend();
                } else {
                  onReflectPlan && onReflectPlan();
                }
              }}
              disabled={chatLoading || reflectLoading || isLoading}
              variant="contained"
              sx={{
                ml: 1,
                ...chatSendButton(featureKey),
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
              startIcon={
                (chatLoading || reflectLoading || isLoading)
                  ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                  : <SendIcon />
              }
            >
              {!planFeatureEnabled || mode === 'chat'
                ? (chatLoading ? '送信中...' : chatButtonLabel)
                : (reflectLoading ? '処理中...' : planButtonLabel)}
            </Button>
          </Box>
        </Box>
      </Rnd>
    </div>
  );
}